/**
 * Shared behaviors for the v19-based homepage sections. Every behavior is null-safe: if a
 * section didn't render the relevant marker (because its Theme Settings effect switch is off),
 * the corresponding query returns nothing and that behavior is a no-op. Motion-driven behaviors
 * (parallax, spotlight) additionally check prefers-reduced-motion at runtime, not just in CSS.
 */
(function () {
  "use strict";

  var reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // ---- Header: solid+blurred once the page scrolls past the transparent-over-hero zone ----
  var siteHeader = document.querySelector("[data-site-header]");
  if (siteHeader) {
    var onScrollHeader = function () {
      siteHeader.classList.toggle("is-stuck", window.scrollY > 60);
    };
    window.addEventListener("scroll", onScrollHeader, { passive: true });
    onScrollHeader();
  }

  // ---- Scroll progress bar ----
  var progress = document.getElementById("hp-progress");
  if (progress) {
    var onScrollProgress = function () {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      var pct = h > 0 ? window.scrollY / h : 0;
      progress.style.transform = "scaleX(" + pct + ")";
    };
    window.addEventListener("scroll", onScrollProgress, { passive: true });
    onScrollProgress();
  }

  // ---- Scroll reveal ----
  // Same failure mode as the counting-up stats below (see the long comment there): with a
  // >0 threshold and no fallback, the IntersectionObserver callback for an element can
  // intermittently never arrive even though the element is already on screen -- so the "rv"
  // class never gets "in" added and the content stays at its pre-reveal opacity/transform
  // forever. Confirmed live with a real repro: fresh load, FAST programmatic scroll-through
  // of the whole page (small steps, ~15ms apart, no long pauses), then checked
  // `.rv:not(.in)` after settling.
  //
  // A first attempt copied the counter fix verbatim -- threshold: 0 plus a single safety-net
  // check on window 'load' (+ a delayed re-check). That is NOT enough here and was proven
  // wrong by the same repro: the counter only ever has one trigger element, effectively
  // always near the top of the page, so a load-time check reliably sees it. ".rv" elements
  // are scattered down the entire page; most of them are still below the fold at load time,
  // so a load-time-only safety net never gets a chance to catch a missed callback for them --
  // it already ran and exited before the user ever scrolled that far. Repro with the load-only
  // version: 20 of 24 ".rv" elements still stuck after a fast scroll-through.
  //
  // Working fix: keep threshold 0 (+ rootMargin) as the primary, cheap path, but back it with
  // a scroll-driven fallback that re-checks every not-yet-revealed element's actual
  // getBoundingClientRect() on every scroll (throttled to one check per animation frame) --
  // not just once at load. That way a missed callback gets caught on the very next scroll
  // frame regardless of where on the page it happened or how fast the user scrolled. The
  // scroll listener removes itself once nothing is left pending.
  var revealEls = document.querySelectorAll(".rv");
  if (revealEls.length && !reduceMotion && "IntersectionObserver" in window) {
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: "0px 0px -5% 0px" }
    );
    revealEls.forEach(function (el, n) {
      el.style.transitionDelay = (n % 3) * 90 + "ms";
      io.observe(el);
    });

    var checkRevealFallback = function () {
      var stillPending = false;
      revealEls.forEach(function (el) {
        if (el.classList.contains("in")) return;
        var r = el.getBoundingClientRect();
        // Top edge above the bottom of the viewport: either still on screen, or already
        // scrolled past entirely -- either way the observer should have fired by now and
        // silently didn't, so reveal it directly. Elements not yet scrolled to (top still
        // below the viewport) are correctly left pending for the observer / a later check.
        if (r.top < window.innerHeight) {
          el.classList.add("in");
          io.unobserve(el);
        } else {
          stillPending = true;
        }
      });
      if (!stillPending) {
        window.removeEventListener("scroll", onRevealScroll);
      }
    };
    var revealTicking = false;
    var onRevealScroll = function () {
      if (revealTicking) return;
      revealTicking = true;
      window.requestAnimationFrame(function () {
        revealTicking = false;
        checkRevealFallback();
      });
    };
    window.addEventListener("scroll", onRevealScroll, { passive: true });
    window.addEventListener("load", function () {
      checkRevealFallback();
      setTimeout(checkRevealFallback, 1200);
    }, { once: true });
  } else {
    revealEls.forEach(function (el) { el.classList.add("in"); });
  }
  // Ausfallsicherung Scroll-Reveal (04.10.2026, pruefer-qa): erst wenn die Einblendung eingerichtet ist, meldet sich das
  // Skript mit html.rv-ready. Fehlt die Klasse (Skript nicht geladen oder vorher abgebrochen), macht assets/homepage.css
  // alle .rv-Elemente nach 3 s sichtbar.
  document.documentElement.classList.add("rv-ready");

  // ---- Counting-up stats ----
  // Was setInterval-driven with a 20%-visibility IntersectionObserver threshold and no
  // fallback -- reliably reproduced live (Welle-3-Kundentests "Kennzahlen-Leiste bleibt bei
  // 0+ stehen"). Root cause confirmed by direct repro (headless Chrome, identical markup):
  // scrolled fully into view and given several seconds, the *first* IntersectionObserver
  // callback intermittently never arrives at all -- with no fallback, startCount() then never
  // runs and every counter is stuck at its initial "0" forever. (requestAnimationFrame was
  // tried and rejected here: it turned out to be the less reliable primitive of the two for
  // driving the actual number updates, so the animation stays setTimeout-based; only the
  // trigger side changes.)
  //
  // Fix has two parts:
  //   1. threshold: 0 (fires as soon as a single pixel is visible, not 20%) plus a bit of
  //      rootMargin, so the observer is less likely to sit exactly on a boundary case.
  //   2. An explicit "already visible and the observer still hasn't fired" safety net on
  //      window 'load' -- belt-and-braces against environments where that first callback
  //      silently never arrives even though the element is already on screen.
  //
  // Amendment (found while fixing the sitewide ".rv" reveal above, same day): the load-only
  // safety net above is NOT sufficient by itself, for exactly the reason explained in the
  // ".rv" comment -- counterTrigger sits several viewport-heights down the homepage, so it is
  // essentially never already on screen at 'load'. Live-reproduced at 390px width: fast
  // scroll-through, then counters left reading "0" with the trigger long since scrolled past.
  // Added a scroll-driven fallback (checkCounterFallback below), same pattern as the ".rv"
  // fix, so a missed callback gets caught on the next scroll frame no matter when it happens.
  var counted = false;
  function startCount() {
    if (counted) return;
    counted = true;
    document.querySelectorAll("[data-count-to]").forEach(function (el) {
      var to = parseInt(el.getAttribute("data-count-to"), 10) || 0;
      if (reduceMotion) {
        el.textContent = to;
        return;
      }
      var duration = 1100;
      var startTime = Date.now();
      var tick = function () {
        var progress = Math.min(1, (Date.now() - startTime) / duration);
        el.textContent = Math.round(to * progress);
        if (progress < 1) {
          setTimeout(tick, 22);
        }
      };
      tick();
    });
  }
  var counterTrigger = document.querySelector("[data-count-trigger]");
  if (counterTrigger && "IntersectionObserver" in window) {
    var countIo = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            startCount();
            countIo.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: "0px 0px -10% 0px" }
    );
    countIo.observe(counterTrigger);

    var checkCounterFallback = function () {
      if (counted) return;
      var r = counterTrigger.getBoundingClientRect();
      // Top edge above the bottom of the viewport: on screen now, or already scrolled past --
      // either way the observer should have fired by now and didn't, so start directly.
      if (r.top < window.innerHeight) {
        startCount();
        countIo.unobserve(counterTrigger);
        window.removeEventListener("scroll", onCounterScroll);
      }
    };
    var counterTicking = false;
    var onCounterScroll = function () {
      if (counterTicking) return;
      counterTicking = true;
      window.requestAnimationFrame(function () {
        counterTicking = false;
        checkCounterFallback();
      });
    };
    window.addEventListener("scroll", onCounterScroll, { passive: true });
    window.addEventListener(
      "load",
      function () {
        checkCounterFallback();
        setTimeout(checkCounterFallback, 1200);
      },
      { once: true }
    );
  } else if (counterTrigger) {
    startCount();
  }

  // ---- Cursor spotlight on cards ----
  if (!reduceMotion) {
    document.querySelectorAll("[data-spotlight]").forEach(function (card) {
      card.addEventListener("mousemove", function (e) {
        var r = card.getBoundingClientRect();
        card.style.setProperty("--mx", e.clientX - r.left + "px");
        card.style.setProperty("--my", e.clientY - r.top + "px");
      });
    });
  }

  // ---- Parallax on photo dividers ----
  var parallaxBgs = document.querySelectorAll("[data-parallax-bg]");
  if (parallaxBgs.length && !reduceMotion) {
    window.addEventListener(
      "scroll",
      function () {
        parallaxBgs.forEach(function (bg) {
          var r = bg.parentElement.getBoundingClientRect();
          if (r.bottom > 0 && r.top < window.innerHeight) {
            bg.style.transform = "scale(1.12) translateY(" + (r.top / window.innerHeight) * -26 + "px)";
          }
        });
      },
      { passive: true }
    );
  }
})();
